import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

const MAX_DISPLAY_NAME_LENGTH = 40;

/**
 * Truncates a display name with "..." if it's longer than maxLength, so long
 * Meta audience names don't blow out the form layout.
 * @param {string} name - Full audience name
 * @param {number} maxLength - Max characters to show before truncating
 * @returns {string} - Truncated (or original) name
 */
function truncateName(name, maxLength = MAX_DISPLAY_NAME_LENGTH) {
  if (!name) return '';
  return name.length > maxLength ? `${name.slice(0, maxLength)}...` : name;
}

/**
 * MetaIdTypeaheadField
 *
 * Generic single-row "ID" input with search-as-you-type suggestions and a
 * name display, used inside the dynamic AudienceFields list for Meta ID lists
 * (custom audiences, income, family statuses, ...). Callers supply the
 * `searchFn` (term -> options) and `lookupFn` (ids -> options) for their ID
 * type. Options are {label, value, name}. The
 * redux-form field value stays a plain audience ID string (unchanged format),
 * so no backend/serialization changes are required.
 *
 * - On mount, resolves an existing ID (e.g. when editing a saved budget) to
 *   its display name via `lookupFn`.
 * - As the user types (text or numbers), search-as-you-type queries active
 *   options matching the input via `searchFn`, showing a dropdown of matches.
 * - Selecting a dropdown option sets the field value to that audience's ID
 *   and shows its name (truncated with "..." if long) to the right of the box.
 */
const MetaIdTypeaheadField = ({
  input, meta, placeholder, accountId, searchFn, lookupFn, extraParams, noResultsMessage, unknownLabel,
}) => {
  const [displayName, setDisplayName] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const user = useSelector(store => store.authentication.user);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);
  const latestRequestRef = useRef(0);
  const hasResolvedInitialValue = useRef(false);

  // On mount, resolve an existing saved ID to its display name.
  useEffect(() => {
    const resolveInitialValue = async () => {
      if (hasResolvedInitialValue.current) return;
      hasResolvedInitialValue.current = true;

      const existingId = (input.value || '').trim();
      if (!existingId) return;

      const [resolved] = await lookupFn({
        resourceNames: [existingId],
        accountId,
        jwtToken: user.jwtToken,
        ...extraParams,
      });
      if (resolved?.name) {
        setDisplayName(resolved.name);
      }
    };

    resolveInitialValue();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Close the dropdown when clicking outside of the field.
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const runSearch = (searchTerm) => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    // Bump the request id so any in-flight response for an older term is ignored.
    latestRequestRef.current += 1;
    const requestId = latestRequestRef.current;

    const trimmedTerm = searchTerm.trim();
    if (!trimmedTerm) {
      setSuggestions([]);
      setIsOpen(false);
      setIsLoading(false);
      setHasSearched(false);
      setHighlightedIndex(-1);
      return;
    }

    setIsLoading(true);
    debounceRef.current = setTimeout(async () => {
      let results = [];
      let failure = '';
      try {
        results = await searchFn({
          accountId,
          searchTerm: trimmedTerm,
          jwtToken: user.jwtToken,
          ...extraParams,
        });
      } catch (error) {
        failure = error?.message || 'Search failed';
      }
      if (requestId !== latestRequestRef.current) return;
      setErrorMessage(failure);
      setSuggestions(results);
      setIsOpen(true);
      setIsLoading(false);
      setHasSearched(true);
      setHighlightedIndex(results.length > 0 ? 0 : -1);

      // If the current value already exactly matches one of the results (e.g.
      // the user finished typing/pasting a full valid ID), resolve its display
      // name immediately without requiring a selection click.
      const exactMatch = results.find(option => option.value === trimmedTerm);
      if (exactMatch) {
        setDisplayName(exactMatch.name);
      }
    }, 350);
  };

  const handleChange = (event) => {
    const newValue = event.target.value;
    input.onChange(newValue);
    setDisplayName('');
    runSearch(newValue);
  };

  const handleSelect = (option) => {
    input.onChange(option.value);
    setDisplayName(option.name);
    setSuggestions([]);
    setIsOpen(false);
    setHasSearched(false);
    setHighlightedIndex(-1);
  };

  const handleKeyDown = (event) => {
    if (!isOpen || suggestions.length === 0) return;

    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev + 1) % suggestions.length);
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlightedIndex(prev => (prev - 1 + suggestions.length) % suggestions.length);
    } else if (event.key === 'Enter') {
      if (highlightedIndex >= 0 && highlightedIndex < suggestions.length) {
        event.preventDefault();
        handleSelect(suggestions[highlightedIndex]);
      }
    } else if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'flex', gap: '8px', alignItems: 'center', width: '100%' }}>
      <div style={{ flex: 1, position: 'relative' }}>
        <div className="form__form-group-input-wrap">
          <input
            name={input.name}
            value={input.value}
            onChange={handleChange}
            onKeyDown={handleKeyDown}
            onFocus={() => setIsOpen(suggestions.length > 0 || hasSearched)}
            onBlur={input.onBlur}
            placeholder={placeholder}
            type="text"
            autoComplete="off"
          />
          {meta?.touched && meta.error && <span className="form__form-group-error">{meta.error}</span>}
        </div>

        {isOpen && (suggestions.length > 0 || (hasSearched && !isLoading)) && (
          <ul
            style={{
              position: 'absolute',
              top: '100%',
              left: 0,
              right: 0,
              zIndex: 20,
              margin: 0,
              padding: '4px 0',
              listStyle: 'none',
              backgroundColor: 'var(--dropdown-bg, #fff)',
              border: '1px solid rgba(0, 0, 0, 0.15)',
              borderRadius: '4px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
              maxHeight: '220px',
              overflowY: 'auto',
            }}
          >
            {suggestions.length === 0 && hasSearched && (
              <li style={{ padding: '6px 10px', color: '#999', fontSize: '0.9em' }}>
                {errorMessage ? `Search failed: ${errorMessage}` : noResultsMessage}
              </li>
            )}
            {suggestions.map((option, index) => (
              <li key={option.value}>
                <button
                  type="button"
                  onMouseDown={event => event.preventDefault()}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  onClick={() => handleSelect(option)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    border: 'none',
                    background: index === highlightedIndex ? 'rgba(145, 190, 35, 0.15)' : 'transparent',
                    padding: '6px 10px',
                    cursor: 'pointer',
                    font: 'inherit',
                    color: 'inherit',
                  }}
                >
                  <span style={{ fontWeight: 500 }}>{truncateName(option.name)}</span>
                  {' '}
                  <span style={{ opacity: 0.6, fontSize: '0.85em' }}>({option.value})</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div
        title={displayName || ''}
        style={{
          flex: '0 0 auto',
          minWidth: '80px',
          maxWidth: '220px',
          fontSize: '0.85em',
          color: displayName ? 'inherit' : '#999',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {isLoading ? 'Searching...' : (truncateName(displayName) || (input.value ? unknownLabel : ''))}
      </div>
    </div>
  );
};

MetaIdTypeaheadField.propTypes = {
  input: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.string,
    onChange: PropTypes.func,
    onBlur: PropTypes.func,
  }).isRequired,
  meta: PropTypes.shape({
    touched: PropTypes.bool,
    error: PropTypes.string,
  }),
  placeholder: PropTypes.string,
  accountId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  searchFn: PropTypes.func.isRequired,
  lookupFn: PropTypes.func.isRequired,
  extraParams: PropTypes.shape(),
  noResultsMessage: PropTypes.string,
  unknownLabel: PropTypes.string,
};

MetaIdTypeaheadField.defaultProps = {
  meta: null,
  placeholder: '',
  accountId: null,
  extraParams: {},
  noResultsMessage: 'No results found',
  unknownLabel: 'Unknown',
};

export default MetaIdTypeaheadField;
